/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import * as api from '@/api/secret';
import { SECRET, COMMON, USER } from '@/store/types';
import { finalizeAction, StoreActionEnv, ActionType } from '@/store/actions';
import { GameItemRooms, SecretRoom } from '@/api/types/secret';
import { StoreDataCache, findCache, saveCache } from '../cache';

export interface StoreSecretRoomState {
  cacheRooms: StoreDataCache<SecretRoom>[];
  cacheRoomsAll: StoreDataCache<GameItemRooms[]>[];
}

export default {
  namespaced: true,
  state: {
    cacheRooms: [],
    cacheRoomsAll: [],
  },
  actions: {
    [SECRET.GET_ROOM_LIST_ALL](
      { state, commit }: StoreActionEnv<StoreSecretRoomState>,
      { action: rawAction = '', scene }: { action?: ActionType; scene: string },
    ) {
      const cache = findCache(state.cacheRoomsAll, scene);
      const action = finalizeAction(rawAction, cache);
      if (action) {
        return new Promise<void>((resolve, reject) => {
          api.getRoomsAll(scene).then((res) => {
            commit(SECRET.GET_ROOM_LIST_ALL, {
              scene,
              data: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [SECRET.GET_ROOM](
      { state, commit }: StoreActionEnv<StoreSecretRoomState>,
      { action: rawAction, id }: { action: ActionType; id: number },
    ) {
      if (!id && rawAction === 'refresh') {
        state.cacheRooms = []; // 清空缓存
        return Promise.resolve();
      }
      const cache = findCache(state.cacheRooms, id);
      const action = finalizeAction(rawAction, !!cache);
      if (action) {
        return new Promise<void>((resolve, reject) => {
          api.getRoom(id).then((res) => {
            commit(SECRET.GET_ROOM, {
              id,
              data: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [SECRET.SET_ROOM]({ dispatch }, params) {
      return new Promise<void>((resolve, reject) => {
        api.putRoom(params.id, params.body).then(() => {
          dispatch(SECRET.GET_ROOM, {
            id: params.id,
            action: 'refresh',
          }).then(resolve).catch(reject);
        }).catch(reject);
      });
    },
    [SECRET.SUBSCRIBE_ROOM]({ commit }, { id, subscribe }) {
      return new Promise<void>((resolve, reject) => {
        api.putRoom(id, {
          subscribed: subscribe,
        }).then(() => {
          store.dispatch(`common/start/${COMMON.GET_START}`, { action: 'refresh' });
          commit(SECRET.SUBSCRIBE_ROOM, {
            id,
            subscribe,
            success: true,
          });
          store.dispatch(`secret/room/${SECRET.GET_ROOM_LIST_ALL}`, { action: 'refresh', scene: 'subscribe' });
          store.dispatch(`secret/room/${SECRET.GET_ROOM}`, { action: 'refresh', id });
          // 订阅操作后刷新首页的订阅数量数据
          store.dispatch(`user/${USER.GET}`, { action: 'refresh' });
          resolve();
        }).catch((err) => {
          commit(SECRET.SUBSCRIBE_ROOM, {
            id,
            success: false,
          });
          reject(err);
        });
      });
    },
  },
  mutations: {
    [SECRET.GET_ROOM_LIST_ALL](state: StoreSecretRoomState, { data, scene }) {
      saveCache(state.cacheRoomsAll, scene, data);
    },
    [SECRET.GET_ROOM](state: StoreSecretRoomState, { data, id }) {
      saveCache(state.cacheRooms, id, data);
    },
    [SECRET.SUBSCRIBE_ROOM](state: StoreSecretRoomState, { id, success, subscribe }) {
      if (success) {
        state.cacheRooms.forEach((item) => {
          if (item.id === id) {
            item.data.subscribed = subscribe;
          }
        });
        state.cacheRoomsAll.forEach((item) => {
          item.data.forEach((group) => {
            group.children.forEach((room) => {
              if (room.id === id) {
                room.subscribed = subscribe;
              }
            });
          });
        });
      }
    },
  },
};
